import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSalesReport, type UseSalesReportFilters } from '../../hooks/useSalesReport';
import { SalesReportAggregatedItem, SalesReportDetailItem, SalesReportSummary } from '../../types/reports';
import { StatusPedido } from '../../types/pedido';
import authService from '../../services/authService';

const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 18mm 16mm 24mm 16mm;
  }

  body {
    background: #ffffff !important;
    color: #0f172a !important;
    font-family: 'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif !important;
  }

  .print-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    animation: fadeIn 0.4s ease-in;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .print-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e2e8f0;
  }

  .print-summary {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }

  .print-summary-card {
    background: linear-gradient(145deg, #f8fbff, #eef2ff);
    border-radius: 16px;
    padding: 16px 20px;
    border: 1px solid rgba(59, 130, 246, 0.18);
    box-shadow: 0 10px 18px rgba(37, 99, 235, 0.08);
  }

  .print-summary-card .label {
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #1d4ed8;
    margin-bottom: 6px;
  }

  .print-summary-card .value {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
  }

  .print-section-title {
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #0f172a;
    margin: 12px 0;
  }

  .print-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .print-table thead th {
    background: #0f172a;
    color: #ffffff;
    padding: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .print-table tbody td {
    border: 1px solid #d9e2ec;
    padding: 10px 12px;
  }

  .print-table tbody tr:nth-of-type(odd) {
    background: #f8fafc;
  }

  @media print {
    .print-hide {
      display: none !important;
    }
  }
`;

const STATUS_OPTIONS: { value: StatusPedido; label: string }[] = [
  { value: StatusPedido.RECEBIDO, label: 'Recebido' },
  { value: StatusPedido.EM_PREPARO, label: 'Em preparo' },
  { value: StatusPedido.PRONTO, label: 'Pronto' },
  { value: StatusPedido.EM_ROTA, label: 'Em rota' },
  { value: StatusPedido.ENTREGUE, label: 'Entregue' },
  { value: StatusPedido.CANCELADO, label: 'Cancelado' },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDateDisplay = (value?: string) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

interface ParsedFilters {
  dataInicio?: string;
  dataFim?: string;
  status: StatusPedido[];
  canais: string[];
  formasPagamento: string[];
  cliente?: string;
  telefone?: string;
  cnpj?: string;
}

const parseFiltersFromSearch = (search: string): ParsedFilters => {
  const params = new URLSearchParams(search);
  const dataInicio = params.get('dataInicio') || undefined;
  const dataFim = params.get('dataFim') || undefined;
  const statusParams = params.getAll('status') as StatusPedido[];
  const canais = params.getAll('canais');
  const formasPagamento = params.getAll('formasPagamento');
  const cliente = params.get('cliente') || undefined;
  const telefone = params.get('telefone') || undefined;
  const cnpj = params.get('cnpj') || undefined;

  const validStatuses = statusParams.filter((status) => Object.values(StatusPedido).includes(status));

  return {
    dataInicio,
    dataFim,
    status: validStatuses.length > 0 ? validStatuses : [StatusPedido.ENTREGUE],
    canais,
    formasPagamento,
    cliente,
    telefone,
    cnpj,
  };
};

const buildQueryFilters = (filters: ParsedFilters, fallbackCnpj?: string): UseSalesReportFilters => {
  const query: UseSalesReportFilters = {};

  if (filters.dataInicio) {
    query.dataInicio = new Date(`${filters.dataInicio}T00:00:00`).toISOString();
  }

  if (filters.dataFim) {
    query.dataFim = new Date(`${filters.dataFim}T23:59:59`).toISOString();
  }

  if (filters.status.length > 0) {
    query.status = filters.status;
  }

  if (filters.canais.length > 0) {
    query.canais = filters.canais;
  }

  if (filters.formasPagamento.length > 0) {
    query.formasPagamento = filters.formasPagamento;
  }

  if (filters.cliente) {
    query.cliente = filters.cliente;
  }

  if (filters.telefone) {
    query.telefone = filters.telefone.replace(/\D/g, '');
  }

  const effectiveCnpj = filters.cnpj || fallbackCnpj;
  if (effectiveCnpj) {
    query.cnpj = effectiveCnpj.replace(/\D/g, '');
  }

  return query;
};

const SalesReportPrintPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useMemo(() => authService.getUser(), []);

  const parsedFilters = useMemo(() => parseFiltersFromSearch(location.search), [location.search]);

  const queryFilters = useMemo(
    () => buildQueryFilters(parsedFilters, user?.cnpj),
    [parsedFilters, user?.cnpj],
  );

  const { data, isLoading, isError } = useSalesReport(queryFilters, true);

  const resumo: SalesReportSummary | undefined = data?.resumo;
  const agregados: SalesReportAggregatedItem[] = data?.agregados ?? [];
  const detalhes: SalesReportDetailItem[] = data?.detalhes ?? [];

  const totalizadores = useMemo(() => {
    if (!resumo) {
      return {
        totalPedidos: 0,
        valorBruto: 0,
        valorLiquido: 0,
        ticketMedio: 0,
      };
    }

    return {
      totalPedidos: resumo.totalPedidos,
      valorBruto: resumo.valorBruto,
      valorLiquido: resumo.valorLiquido,
      ticketMedio: resumo.ticketMedio,
    };
  }, [resumo]);

  const statusLabels = useMemo(() => {
    if (parsedFilters.status.length === 0) {
      return 'Todos';
    }
    return parsedFilters.status
      .map((status) => STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status)
      .join(', ');
  }, [parsedFilters.status]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f6fb',
        py: 4,
        px: { xs: 2, md: 6 },
      }}
    >
      <style>{PRINT_STYLES}</style>

      <Box
        className="print-hide"
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Visualização para impressão
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revise as informações e clique em "Imprimir" para gerar o PDF. Você pode voltar e ajustar os filtros quando precisar.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} disabled={isLoading}>
            Imprimir
          </Button>
        </Box>
      </Box>

      {isError && (
        <Alert severity="error" className="print-hide" sx={{ mb: 3 }}>
          Não foi possível carregar os dados do relatório. Volte e ajuste os filtros para tentar novamente.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: '#ffffff',
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.12)',
          }}
        >
          <div className="print-wrapper">
            <div className="print-header">
              <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Relatório de vendas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Emitido em {formatDateTime(new Date().toISOString())}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Período: {formatDateDisplay(parsedFilters.dataInicio)} – {formatDateDisplay(parsedFilters.dataFim)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status considerados: {statusLabels}
                </Typography>
                {parsedFilters.canais.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Canais: {parsedFilters.canais.join(', ')}
                  </Typography>
                )}
                {parsedFilters.formasPagamento.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Formas de pagamento: {parsedFilters.formasPagamento.join(', ')}
                  </Typography>
                )}
              </Box>
              <Box textAlign="right">
                {user?.name && (
                  <Typography variant="body2" color="text.secondary">
                    Responsável: {user.name}
                  </Typography>
                )}
                {(parsedFilters.cnpj || user?.cnpj) && (
                  <Typography variant="body2" color="text.secondary">
                    CNPJ: {(parsedFilters.cnpj || user?.cnpj) ?? ''}
                  </Typography>
                )}
              </Box>
            </div>

            <div className="print-summary">
              <div className="print-summary-card">
                <div className="label">Total de pedidos</div>
                <div className="value">{totalizadores.totalPedidos}</div>
              </div>
              <div className="print-summary-card">
                <div className="label">Valor bruto</div>
                <div className="value">{formatCurrency(totalizadores.valorBruto)}</div>
              </div>
              <div className="print-summary-card">
                <div className="label">Valor líquido</div>
                <div className="value">{formatCurrency(totalizadores.valorLiquido)}</div>
              </div>
              <div className="print-summary-card">
                <div className="label">Ticket médio</div>
                <div className="value">{formatCurrency(totalizadores.ticketMedio)}</div>
              </div>
            </div>

            <Divider />

            <div>
              <div className="print-section-title">Resumo diário</div>
              {agregados.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum registro agregado encontrado para o período selecionado.
                </Typography>
              ) : (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Data</th>
                      <th style={{ textAlign: 'right' }}>Pedidos</th>
                      <th style={{ textAlign: 'right' }}>Valor dos itens</th>
                      <th style={{ textAlign: 'right' }}>Taxas</th>
                      <th style={{ textAlign: 'right' }}>Descontos</th>
                      <th style={{ textAlign: 'right' }}>Valor total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agregados.map((item: SalesReportAggregatedItem) => (
                      <tr key={item.referencia}>
                        <td>{item.referencia}</td>
                        <td style={{ textAlign: 'right' }}>{item.quantidadePedidos}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.valorItens)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.valorTaxas)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.descontoAplicado)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.valorTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <Divider />

            <div>
              <div className="print-section-title">Pedidos detalhados</div>
              {detalhes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum pedido detalhado encontrado para os filtros aplicados.
                </Typography>
              ) : (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Data</th>
                      <th style={{ textAlign: 'left' }}>Cliente</th>
                      <th style={{ textAlign: 'left' }}>Telefone</th>
                      <th style={{ textAlign: 'left' }}>Canal</th>
                      <th style={{ textAlign: 'left' }}>Status</th>
                      <th style={{ textAlign: 'left' }}>Pagamento</th>
                      <th style={{ textAlign: 'right' }}>Itens</th>
                      <th style={{ textAlign: 'right' }}>Taxa</th>
                      <th style={{ textAlign: 'right' }}>Desconto</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhes.map((pedido: SalesReportDetailItem) => (
                      <tr key={pedido.id}>
                        <td>{formatDateTime(pedido.dataPedido)}</td>
                        <td>{pedido.cliente}</td>
                        <td>{pedido.telefone || '—'}</td>
                        <td>{pedido.canal}</td>
                        <td>{pedido.status}</td>
                        <td>{pedido.formaPagamento || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(pedido.valorItens)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(pedido.valorTaxa)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(pedido.desconto)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(pedido.valorTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </Box>
      )}
    </Box>
  );
};

export default SalesReportPrintPage;
